/**
 * Bench: internal and unassigned capacity, and what excluding it is worth.
 *
 * THE RECONCILIATION IS THE PAGE. Not the table -- the table is a breakdown of
 * one number, and a screen that led with it would be a cost report. What makes
 * this screen worth having is the pair of margins: every other figure in the
 * product excludes this capacity, and here is the same company with it put
 * back. 46.53% against 18.17% is not a rounding difference, it is a different
 * business, and the 28.36 points between them is the size of the choice.
 *
 * So both margins and the gap are the largest thing on the screen, the
 * breakdown sits underneath, and `unattributed_cost` appears only when it is
 * non-zero -- that figure is the two bases disagreeing about what counts as
 * bench, which is worth saying when it happens and worth nothing when it does
 * not.
 */
import { Users } from "lucide-react";
import { HBars } from "@/components/charts/HBars";
import { PageHeaderBar } from "@/components/layout/PageHeaderBar";
import { usePageHeader } from "@/components/layout/PageHeaderContext";
import { CostBasisNote } from "@/components/profitability/CostBasisNote";
import { DataTable, ROW_CLASS, TOTAL_ROW_CLASS } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { HeroPanel } from "@/components/shared/hero-panel";
import { InlineBar } from "@/components/shared/inline-bar";
import { KpiTile } from "@/components/shared/kpi-tile";
import { PanelTitle } from "@/components/shared/panel-title";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useBench } from "@/hooks/use-bench";
import { useRangeParam } from "@/hooks/use-range-param";
import { COLUMN_HELP } from "@/lib/column-help";
import { hours as formatHours, money, percent } from "@/lib/format";

export function BenchPage() {
  const [range, setRange] = useRangeParam();
  const { data, isLoading, isError, error, refetch } = useBench(range);

  const categories = data?.categories ?? [];
  const rec = data?.reconciliation;
  const byPeriod = data?.by_period ?? [];

  const rangeInfo = data?.range;
  // Published before the error branch, so a failed load still shows the title
  // and the range that produced it rather than an unlabelled error.
  //
  // No subtitle: "the cost every other margin in this product leaves out" is
  // said again, with the figures attached, by the reconciliation hero directly
  // below, whose note opens by calling the excluding-bench margin "the headline
  // figure everywhere else in this product".
  usePageHeader(
    () => (
      <PageHeaderBar title="Bench" range={rangeInfo} rangeValue={range} onRangeChange={setRange} />
    ),
    [rangeInfo, range, setRange],
  );

  if (isError) {
    return (
      <div className="w-full space-y-4">
        <ErrorState message={(error as Error)?.message} onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      {/* The reconciliation, first and biggest. The engine's own note rides
          under the figure: it names the dollar figure and says explicitly that
          the capacity is attributed rather than netted away, which is the claim
          the table below has to back up. */}
      <HeroPanel
        eyebrow="Company margin, excluding bench"
        figure={percent(rec?.margin_excluding_bench_pct)}
        note={
          rec
            ? `${
                rec.headline_basis === "excluding_bench"
                  ? "The headline figure everywhere else in this product."
                  : "Not the headline basis."
              } ${rec.note}`
            : undefined
        }
        facts={[
          {
            label: "Including bench",
            value: percent(rec?.margin_including_bench_pct),
            help: "The same revenue with this capacity's cost put back in.",
          },
          {
            label: "What the exclusion is worth",
            value:
              rec?.bench_costs_points === null || rec?.bench_costs_points === undefined
                ? "—"
                : `${rec.bench_costs_points.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} pts`,
            tone: "bad",
            help: "Percentage points, not dollars. The distance between the two figures beside it.",
          },
        ]}
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <KpiTile label="Bench cost" value={data ? money(data.bench_cost) : "—"} />
        <KpiTile label="Bench hours" value={data ? formatHours(data.bench_hours) : "—"} />
        <KpiTile
          label="Bench revenue"
          value={data ? money(data.bench_revenue) : "—"}
          help={
            data && data.bench_revenue === 0
              ? "None. This capacity earns nothing by definition."
              : "Revenue booked against internal capacity."
          }
        />
      </div>

      {/* Shown only when the two bases disagree. A permanent "$0 unattributed"
          line would train the reader to skip the one row that matters. */}
      {data && data.unattributed_cost !== 0 ? (
        <div
          role="alert"
          className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm"
        >
          <p className="font-semibold">
            {money(data.unattributed_cost)} of bench cost is not attributed to an offering
          </p>
          <p className="mt-1 text-muted-foreground">
            The total bench cost and the sum of the categories below disagree by this much, which
            means the two bases do not agree about what counts as bench. The shares in the table are
            shares of {money(data.attributed_cost)}, not of {money(data.bench_cost)}.
          </p>
        </div>
      ) : null}

      <div
        className={
          byPeriod.length > 0 ? "grid gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]" : ""
        }
      >
        <Card flush className="w-full">
          <CardHeader>
            <PanelTitle>Bench cost by offering</PanelTitle>
          </CardHeader>
          <DataTable
            flush
            loading={isLoading}
            headers={[
              { label: "Offering", help: COLUMN_HELP.benchOffering },
              { label: "Cost", className: "text-right", help: COLUMN_HELP.cost },
              { label: "Hours", className: "text-right", help: COLUMN_HELP.hours },
              { label: "Share", className: "text-right", help: COLUMN_HELP.benchShare },
            ]}
          >
            {categories.map((row) => (
              <tr key={row.offering} className={ROW_CLASS}>
                <td className="px-4 py-3 font-medium">{row.offering}</td>
                <td className="px-4 py-3 text-right tabular-nums">{money(row.cost)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{formatHours(row.hours)}</td>
                <td className="px-4 py-3 text-right">
                  {row.share_pct === null ? (
                    "—"
                  ) : (
                    <InlineBar
                      value={row.share_pct / 100}
                      mode="share"
                      fill="series2"
                      label={percent(row.share_pct)}
                      className="justify-end"
                    />
                  )}
                </td>
              </tr>
            ))}
            {data && !isLoading ? (
              <tr className={TOTAL_ROW_CLASS}>
                <td className="px-4 py-3">Attributed</td>
                <td className="px-4 py-3 text-right tabular-nums">{money(data.attributed_cost)}</td>
                <td className="px-4 py-3" />
                <td className="px-4 py-3" />
              </tr>
            ) : null}
          </DataTable>
        </Card>

        {byPeriod.length > 0 ? (
          <Card className="w-full">
            <CardHeader>
              <PanelTitle>Bench cost by month</PanelTitle>
            </CardHeader>
            <CardContent>
              <HBars
                label="Bench cost by month"
                height={Math.max(118, byPeriod.length * 24 + 12)}
                rows={byPeriod.map((p) => ({
                  label: p.period ?? p.period_id,
                  value: p.cost,
                  display: money(p.cost),
                  series: 2,
                }))}
              />
            </CardContent>
          </Card>
        ) : null}
      </div>

      {!isLoading && categories.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No bench cost in this range"
          description="Nobody booked time to internal or unassigned capacity in the selected months."
        />
      ) : null}

      <CostBasisNote basis={data?.cost_basis} />
    </div>
  );
}
